<template>
    <div class="authPage">
        <div class="authCard">
            <h1>Sign in</h1>
            <p v-if="status === 'loading'">Starting MFA challenge…</p>

            <div v-else-if="status === 'code'">
                <p>A 6-digit code was sent to your email.</p>
                <p v-if="formattedSentAt" class="sentAtNote">Sent at {{ formattedSentAt }}</p>
                <form @submit.prevent="submitCode" class="codeForm">
                    <label for="mfaCode" class="label">6-digit code</label>
                    <input
                        id="mfaCode"
                        ref="mfaCodeInput"
                        class="form-control"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        maxlength="6"
                        v-model="code"
                    />
                    <button class="btn btn-primary" type="submit" :disabled="submitting || normalizedCode.length !== 6">
                        {{ submitting ? "Verifying…" : "Verify" }}
                    </button>
                </form>
            </div>

            <div v-else-if="status === 'error'">
                <p class="text-danger">{{ error }}</p>
                <button class="btn btn-secondary" type="button" @click="start({ force: true })">Try again</button>
            </div>
        </div>
    </div>
</template>

<script>
import { apiFetchJson } from "@/libs/api";
import { setAuth, updateAuth, getUserId } from "@/libs/auth";

const MFA_CACHE_PREFIX = "qrce.mfa.challenge.";
const MFA_CACHE_TTL_MS = 5 * 60 * 1000;

export default {
    data() {
        return {
            status: "loading", // loading | code | error
            userId: null,
            mfaToken: null,
            sentAt: null,
            expiresAt: null,
            code: "",
            submitting: false,
            error: "",
        };
    },
    computed: {
        normalizedCode() {
            return `${this.code || ""}`.replace(/\D/g, "").slice(0, 6);
        },
        formattedSentAt() {
            if (!this.sentAt) return "";
            try {
                return new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                }).format(new Date(this.sentAt));
            } catch {
                return this.sentAt;
            }
        },
    },
    methods: {
        getChallengeCacheKey(userId) {
            return `${MFA_CACHE_PREFIX}${encodeURIComponent(userId)}`;
        },
        readCachedChallenge(userId) {
            if (typeof window === "undefined" || !userId) return null;
            try {
                const raw = window.sessionStorage.getItem(this.getChallengeCacheKey(userId));
                if (!raw) return null;
                const parsed = JSON.parse(raw);
                if (!parsed?.mfaToken) return null;

                const cachedAtMs = Date.parse(parsed.cachedAt || "");
                const expiresAtMs = Date.parse(parsed.expiresAt || "");
                const now = Date.now();
                const cacheExpired =
                    (Number.isFinite(cachedAtMs) && now - cachedAtMs > MFA_CACHE_TTL_MS) ||
                    (Number.isFinite(expiresAtMs) && now >= expiresAtMs);

                if (cacheExpired) {
                    this.clearCachedChallenge(userId);
                    return null;
                }
                return parsed;
            } catch {
                return null;
            }
        },
        writeCachedChallenge(userId, challenge) {
            if (typeof window === "undefined" || !userId || !challenge?.mfaToken) return;
            try {
                window.sessionStorage.setItem(
                    this.getChallengeCacheKey(userId),
                    JSON.stringify({
                        mfaToken: challenge.mfaToken,
                        sentAt: challenge.sentAt || null,
                        expiresAt: challenge.expiresAt || null,
                        cachedAt: new Date().toISOString(),
                    })
                );
            } catch {
                // ignore storage issues
            }
        },
        clearCachedChallenge(userId = this.userId) {
            if (typeof window === "undefined" || !userId) return;
            try {
                window.sessionStorage.removeItem(this.getChallengeCacheKey(userId));
            } catch {
                // ignore storage issues
            }
        },
        focusCodeInput() {
            try {
                const el = this.$refs.mfaCodeInput;
                if (el && typeof el.focus === "function") el.focus();
            } catch {
                // ignore
            }
        },
        async start(options = {}) {
            const { force = false } = options;
            this.status = "loading";
            this.error = "";
            this.code = "";
            this.mfaToken = null;
            this.sentAt = null;
            this.expiresAt = null;

            const routeId = this.$route.query.auth;
            const storedId = getUserId();
            const userId = routeId || storedId;
            if (!userId) {
                this.status = "error";
                this.error = "Missing user id.";
                return;
            }
            this.userId = userId;
            updateAuth({ userId });

            if (!force) {
                const cachedChallenge = this.readCachedChallenge(userId);
                if (cachedChallenge) {
                    this.mfaToken = cachedChallenge.mfaToken;
                    this.sentAt = cachedChallenge.sentAt || null;
                    this.expiresAt = cachedChallenge.expiresAt || null;
                    this.status = "code";
                    this.$nextTick(() => this.focusCodeInput());
                    return;
                }
            } else {
                this.clearCachedChallenge(userId);
            }

            try {
                const data = await apiFetchJson(`users/mfa/${encodeURIComponent(userId)}`, {}, { auth: false });
                if (!data?.mfaToken) {
                    this.status = "error";
                    this.error = "MFA challenge could not be started.";
                    return;
                }
                this.mfaToken = data.mfaToken;
                this.sentAt = data.sentAt || null;
                this.expiresAt = data.expiresAt;
                this.writeCachedChallenge(userId, data);
                this.status = "code";
                this.$nextTick(() => this.focusCodeInput());
            } catch (err) {
                this.status = "error";
                this.error = err?.message || "Failed to start MFA challenge.";
            }
        },
        async submitCode() {
            if (!this.mfaToken || !this.userId) return;
            const code = this.normalizedCode;
            if (code.length !== 6) return;

            this.submitting = true;
            this.error = "";
            try {
                const validate = await apiFetchJson(
                    "users/mfavalidate",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ mfaToken: this.mfaToken, code }),
                    },
                    { auth: false }
                );
                if (!validate?.accessToken) {
                    throw new Error("MFA validation did not return an access token.");
                }
                this.clearCachedChallenge(this.userId);
                setAuth({
                    userId: this.userId,
                    accessToken: validate.accessToken,
                    expiresAt: validate.expiresAt,
                    tokenType: validate.tokenType,
                });

                // Prefer user details returned by mfavalidate; fall back to GET /users/{id}
                const userFromValidate = validate?.user && typeof validate.user === "object" ? validate.user : null;
                if (!userFromValidate) {
                    await apiFetchJson(`users/${encodeURIComponent(this.userId)}`, {}, { auth: true });
                }

                this.$router.push({ path: "/" });
            } catch (err) {
                if ((err?.message || "").includes("expired")) {
                    this.clearCachedChallenge(this.userId);
                }
                this.error = err?.message || "MFA validation failed.";
                this.status = "error";
            } finally {
                this.submitting = false;
            }
        },
    },
    mounted() {
        this.start();
    },
};
</script>

<style scoped>
.authPage {
    padding: 20px 12px;
}
.authCard {
    max-width: 586px;
    margin: 20px auto;
    background: var(--qrce-surface);
    text-align: left;
    padding: 20px;
    border-radius: var(--qrce-radius);
    border: 1px solid var(--qrce-border);
    border-left: 4px solid var(--qrce-primary);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.10);
    box-sizing: border-box;
}
.label {
    font-size: 0.8em;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
}
.authCard :deep(input.form-control),
.authCard :deep(select.form-control),
.authCard :deep(textarea.form-control) {
    background: var(--qrce-surface-2);
    border: 1px solid rgba(15, 23, 42, 0.22);
    border-radius: 12px;
    color: var(--qrce-text);
    padding: 10px 12px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}
.authCard :deep(input.form-control:hover),
.authCard :deep(select.form-control:hover),
.authCard :deep(textarea.form-control:hover) {
    border-color: rgba(15, 23, 42, 0.32);
}
.authCard :deep(input.form-control::placeholder),
.authCard :deep(textarea.form-control::placeholder) {
    color: rgba(15, 23, 42, 0.55);
}
.codeForm {
    display: grid;
    gap: 10px;
}

.sentAtNote {
    margin-top: -4px;
    color: var(--qrce-muted);
    font-size: 0.95em;
}

@media (max-width: 819px) {
    .authCard {
        width: calc(100% - 24px);
        margin: 14px 12px;
    }
}
</style>
